/**
 * DriveApp — full auth Drive Preact UI (chrome + list + modals).
 * Mounted into #drive-controller by modules/drive/mount.js.
 */

import { h } from 'preact'
import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks'
import { DriveStoreContext, DriveI18nContext, DriveUiContext } from './drive-context.js'
import { DriveContent } from './DriveContent.jsx'
import { DriveToolbar } from './DriveToolbar.jsx'
import { DriveFilters } from './DriveFilters.jsx'
import { DriveBreadcrumb } from './DriveBreadcrumb.jsx'
import { RenameModal } from './RenameModal.jsx'
import { ShareModal } from './ShareModal.jsx'
import { PermissionsModal } from './PermissionsModal.jsx'
import { MoveModal } from './MoveModal.jsx'
import { NewFolderModal } from './NewFolderModal.jsx'
import { UploadModal } from './UploadModal.jsx'
import { QuotaExceededModal, useQuotaGate } from '../shared/quota/index.js'
import { isBlockingMode, normalizeQuota } from '../shared/quota/parseQuotaError.js'

export { DriveStoreContext, DriveI18nContext, DriveUiContext, useDriveStore, useDriveI18n, useDriveUi } from './drive-context.js'

function isVirtualFolder(folder) {
  if (!folder) return false
  if (folder.virtual) return true
  const id = folder.id
  if (id == null) return false
  const s = String(id)
  return s === 'tasks' || s === 'equipments' || s.startsWith('tasks-') || s.startsWith('equipments-') ||
    (typeof id === 'string' && id.startsWith('bc-') && !folder.path)
}

/** Boot / 402 storageQuota with hard_stop (or other blocking mode) and used >= limit. */
export function isStorageHardStopExhausted(rawQuota) {
  const q = normalizeQuota(rawQuota)
  if (!q?.exceeded) return false
  return isBlockingMode(q.barrierMode)
}

export function DriveApp({
  store,
  i18n,
  host,
  canCreate = false,
  canUpdate = false,
  canDestroy = false,
  canTagFiles = false,
  editorEnabled = false,
  onlyOfficeEntitled = false,
  onlyOfficeLockTitle = '',
  onlyOfficeUpgradePath = '',
  webdavEnabled = false,
  publicShareAllowed = false,
  publicShareLockTitle = '',
  publicShareUpgradePath = '',
  // Optional usage-barrier status for the drive_storage metric. Same shape as
  // the HTTP 402 `quota_exceeded` body; when absent the quota UI stays hidden.
  storageQuota = null
}) {
  const fileInputRef = useRef(null)
  const folderInputRef = useRef(null)
  const dropzoneRef = useRef(null)
  const gate = useQuotaGate()

  const [renameTarget, setRenameTarget] = useState(null)
  const [shareTarget, setShareTarget] = useState(null)
  const [permissionsTarget, setPermissionsTarget] = useState(null)
  const [moveOpen, setMoveOpen] = useState(false)
  const [newFolderOpen, setNewFolderOpen] = useState(false)

  const openRename = useCallback((target) => setRenameTarget(target), [])
  const closeRename = useCallback(() => setRenameTarget(null), [])

  const openShare = useCallback((target) => setShareTarget(target), [])
  const openPermissions = useCallback((target) => setPermissionsTarget(target), [])
  const openMove = useCallback(() => setMoveOpen(true), [])
  const openNewFolder = useCallback(() => setNewFolderOpen(true), [])

  const requestMove = useCallback((itemKey) => {
    store.selectOnly(itemKey)
    setMoveOpen(true)
  }, [store])

  const storageHardStopExhausted = isStorageHardStopExhausted(storageQuota)

  const openQuotaModal = useCallback(() => {
    if (!storageQuota) return
    gate.captureBody(storageQuota, 402)
  }, [gate.captureBody, storageQuota])

  const reportQuotaExceeded = useCallback((quota) => {
    if (!quota) return
    gate.captureBody(quota.raw ?? quota, 402)
  }, [gate.captureBody])

  const triggerUploadFiles = useCallback(() => {
    if (storageHardStopExhausted) {
      openQuotaModal()
      return
    }
    fileInputRef.current?.click()
  }, [storageHardStopExhausted, openQuotaModal])

  const triggerUploadFolder = useCallback(() => {
    if (storageHardStopExhausted) {
      openQuotaModal()
      return
    }
    folderInputRef.current?.click()
  }, [storageHardStopExhausted, openQuotaModal])

  useEffect(() => {
    if (!host) return undefined
    host.onQuotaExceeded = reportQuotaExceeded
    return () => {
      if (host.onQuotaExceeded === reportQuotaExceeded) delete host.onQuotaExceeded
    }
  }, [host, reportQuotaExceeded])

  const shareSelected = useCallback(() => {
    const ids = store.selectedIds.value
    if (ids.length !== 1) return
    const key = ids[0]
    if (key.startsWith('file-')) openShare({ type: 'file', id: key.replace('file-', '') })
    else if (key.startsWith('folder-')) openShare({ type: 'folder', id: key.replace('folder-', '') })
  }, [store, openShare])

  const ui = useMemo(() => ({
    canCreate: !!canCreate,
    canUpdate: !!canUpdate,
    canDestroy: !!canDestroy,
    canTagFiles: !!canTagFiles,
    editorEnabled: !!editorEnabled,
    onlyOfficeEntitled: !!onlyOfficeEntitled,
    onlyOfficeLockTitle,
    onlyOfficeUpgradePath,
    webdavEnabled: !!webdavEnabled,
    publicShareAllowed: !!publicShareAllowed,
    publicShareLockTitle,
    publicShareUpgradePath,
    storageQuota,
    storageHardStopExhausted,
    openQuotaModal,
    reportQuotaExceeded,
    isVirtualFolder,
    openRename,
    requestMove,
    openShare,
    openPermissions,
    openMove,
    openNewFolder,
    triggerUploadFiles,
    triggerUploadFolder,
    shareSelected,
    downloadSelected: () => host?.downloadSelected?.(),
    deleteSelected: () => host?.deleteSelected?.(),
    cancelUpload: () => host?.cancelUploadModal?.()
  }), [
    canCreate, canUpdate, canDestroy, canTagFiles, editorEnabled, onlyOfficeEntitled,
    onlyOfficeLockTitle, onlyOfficeUpgradePath, webdavEnabled, publicShareAllowed,
    publicShareLockTitle, publicShareUpgradePath, storageQuota, storageHardStopExhausted,
    openQuotaModal, reportQuotaExceeded, openRename, requestMove, openShare,
    openPermissions, openMove, openNewFolder, triggerUploadFiles, triggerUploadFolder,
    shareSelected, host
  ])

  // Expose modal openers for FileRow RowActions that still dispatch events / host
  if (typeof window !== 'undefined') {
    window._driveUi = ui
  }

  return (
    <DriveStoreContext.Provider value={store}>
      <DriveI18nContext.Provider value={i18n}>
        <DriveUiContext.Provider value={ui}>
          <DriveToolbar />
          <DriveFilters />
          <DriveBreadcrumb />

          <div class="drive-file-list" ref={dropzoneRef} data-drive-dropzone="1">
            <div class="drive-drop-overlay-text">📁 {i18n.t('drive.drop_files_here')}</div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              style="position: absolute; width: 1px; height: 1px; opacity: 0; overflow: hidden; clip: rect(0,0,0,0); pointer-events: none;"
              onChange={(e) => {
                const files = Array.from(e.target.files || [])
                e.target.value = ''
                host?.uploadPlainFiles?.(files)
              }}
            />
            <input
              ref={folderInputRef}
              type="file"
              webkitdirectory
              multiple
              style="position: absolute; width: 1px; height: 1px; opacity: 0; overflow: hidden; clip: rect(0,0,0,0); pointer-events: none;"
              onChange={(e) => host?.uploadFolderFromInput?.(e)}
            />

            <div id="drive-content-area" class="drive-content-wrapper">
              <DriveContent />
            </div>
          </div>

          <div class="keyboard-help d-none d-md-block text-center py-3 border-top">
            <small class="text-muted">
              <i class="bi bi-keyboard me-1" />
              {i18n.t('drive.keyboard_help')}
            </small>
          </div>

          <RenameModal target={renameTarget} onClose={closeRename} />
          <ShareModal open={!!shareTarget} target={shareTarget} onClose={() => setShareTarget(null)} />
          <PermissionsModal open={!!permissionsTarget} target={permissionsTarget} onClose={() => setPermissionsTarget(null)} />
          <MoveModal open={moveOpen} onClose={() => setMoveOpen(false)} />
          <NewFolderModal open={newFolderOpen} onClose={() => setNewFolderOpen(false)} />
          <UploadModal onCancel={() => host?.cancelUploadModal?.()} />
          <QuotaExceededModal
            open={gate.open}
            quota={gate.quota}
            t={(key, opts) => i18n.t(key, opts)}
            format="bytes"
            onClose={gate.dismiss}
          />
        </DriveUiContext.Provider>
      </DriveI18nContext.Provider>
    </DriveStoreContext.Provider>
  )
}

export function getDriveDropzone(root) {
  return root?.querySelector('[data-drive-dropzone="1"]')
}
